import { useTranslation } from "react-i18next"

import { TiktokLinkStateCue } from "@/components/tiktok-link-state-cue"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { toAppLocale } from "@/lib/i18n/locales"
import { formatStrictDistance } from "@/lib/relative-time"
import type { TimeseriesProps } from "@/types/creator-analytics"

import { CreatorAnalyticsLineChart } from "./creator-analytics-line-chart"

export interface CreatorAnalyticsTimeseriesCardProps {
  timeseries: TimeseriesProps
}

/**
 * OKS-64 — Time-series card hosting the follower-growth + engagement-
 * trend charts stacked vertically (UX Spec §2).
 *
 * Shows the `TiktokLinkStateCue` pill above the charts when
 * `oauth_state === "refresh_needed"` or `"revoked"` (UX Spec §3).
 * For `oauth_state === "active"`, no pill renders.
 *
 * "Last synced X ago" watermark is computed FE-side via
 * `formatStrictDistance` (date-fns strict distance) so the value stays
 * reactively-fresh without a server re-render (TRD K13). OKS-1025: the
 * fragment is localised from i18next, so the fr template reads
 * « Synchronisé il y a 2 heures », not « Synchronisé 2 hours ago ».
 */
export function CreatorAnalyticsTimeseriesCard({
  timeseries,
}: CreatorAnalyticsTimeseriesCardProps) {
  const { t, i18n } = useTranslation()

  const linkState =
    timeseries.oauth_state === "refresh_needed" ||
    timeseries.oauth_state === "revoked"
      ? timeseries.oauth_state
      : null

  const lastSyncedCopy = timeseries.last_synced_at
    ? t("crew.analytics.timeseries.last_synced", {
        relative: formatStrictDistance(
          timeseries.last_synced_at,
          toAppLocale(i18n.language),
          { addSuffix: true },
        ),
      })
    : t("crew.analytics.timeseries.last_synced_unknown")

  const followerStart = timeseries.follower_series[0]?.date ?? ""
  const followerEnd =
    timeseries.follower_series[timeseries.follower_series.length - 1]?.date ??
    ""
  const followerCurrent =
    timeseries.follower_series[timeseries.follower_series.length - 1]?.value ??
    0
  const followerStartValue = timeseries.follower_series[0]?.value ?? 0
  const followerDelta = followerCurrent - followerStartValue

  const engagementCurrent =
    timeseries.engagement_series[timeseries.engagement_series.length - 1]
      ?.value ?? 0

  return (
    <Card
      data-testid="creator-analytics-timeseries"
      aria-label={t("crew.analytics.timeseries.section_aria")}
    >
      <CardHeader className="flex flex-row items-baseline justify-between">
        <CardTitle>{t("crew.analytics.timeseries.title")}</CardTitle>
        <span
          className="text-muted-foreground text-sm"
          data-testid="creator-analytics-timeseries-last-synced"
        >
          {lastSyncedCopy}
        </span>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {linkState && (
          <div
            className="flex items-center gap-2"
            data-testid="creator-analytics-timeseries-frozen"
            data-state={linkState}
          >
            <TiktokLinkStateCue state={linkState} />
            <span className="text-muted-foreground text-xs">
              {t(`crew.analytics.timeseries.${linkState}.caption`, {
                date: followerEnd,
              })}
            </span>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">
            {t("crew.analytics.timeseries.follower_chart.title")}
          </h3>
          <CreatorAnalyticsLineChart
            chartId="follower"
            data={timeseries.follower_series}
            colorVar="--chart-1"
            ariaLabel={t(
              "crew.analytics.timeseries.follower_chart.aria_label",
              {
                start: followerStart,
                end: followerEnd,
                current: followerCurrent,
                delta: followerDelta,
              },
            )}
            unit={t("crew.analytics.timeseries.follower_chart.y_axis")}
            mirrorTableHeading={t(
              "crew.analytics.timeseries.follower_chart.title",
            )}
          />
        </div>

        <Separator />

        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">
            {t("crew.analytics.timeseries.engagement_chart.title")}
          </h3>
          <CreatorAnalyticsLineChart
            chartId="engagement"
            data={timeseries.engagement_series}
            colorVar="--chart-2"
            ariaLabel={t(
              "crew.analytics.timeseries.engagement_chart.aria_label",
              {
                start: followerStart,
                end: followerEnd,
                current: engagementCurrent,
              },
            )}
            unit={t("crew.analytics.timeseries.engagement_chart.unit")}
            mirrorTableHeading={t(
              "crew.analytics.timeseries.engagement_chart.title",
            )}
          />
        </div>
      </CardContent>
    </Card>
  )
}

export default CreatorAnalyticsTimeseriesCard
